import { useEffect, useState } from "react";
import {
  api,
  ApiError,
  APP_PLATFORM_LABEL,
  fmtDateTime,
  type AppVersionPatch,
  type AppVersionRow,
} from "../api";
import { ConfirmDialog } from "../components/ConfirmDialog";

/** Tahrirlanadigan maydonlar — server xato kodlari shularga bog'lanadi. */
type FieldKey = "latest_version" | "min_supported_version" | "store_url";

// Server xatosi qaysi maydonga tegishli. Bu ro'yxatda yo'q kod (masalan tarmoq
// xatosi) kartaning tepasidagi umumiy bannerda ko'rsatiladi.
const ERROR_FIELD: Record<string, FieldKey> = {
  latest_version_invalid: "latest_version",
  latest_version_downgrade: "latest_version",
  min_version_invalid: "min_supported_version",
  min_version_exceeds_latest: "min_supported_version",
  store_url_invalid: "store_url",
};

// Minimal versiya KO'TARILGANIDA ogohlantirish kerak. Taqqoslash sonli bo'lishi
// shart (1.10.0 > 1.9.0). Backend'dagi `core/semver.py` — yagona haqiqiy manba;
// bu faqat dialogni ko'rsatish/ko'rsatmaslik uchun.
function parseVersion(v: string): [number, number, number] | null {
  const m = /^(\d+)\.(\d+)\.(\d+)$/.exec(v.trim());
  if (!m) return null;
  return [Number(m[1]), Number(m[2]), Number(m[3])];
}

function isRaise(from: string, to: string): boolean {
  const a = parseVersion(from);
  const b = parseVersion(to);
  if (!a || !b) return false;
  for (let i = 0; i < 3; i++) {
    if (b[i] > a[i]) return true;
    if (b[i] < a[i]) return false;
  }
  return false;
}

export function AppVersion() {
  const [rows, setRows] = useState<AppVersionRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        setErr(null);
        setRows(await api.appVersions());
      } catch (e) { setErr((e as Error).message); }
    })();
  }, []);

  // Saqlangan qatorni PATCH javobidan joyida almashtiramiz — qayta yuklash
  // kartani unmount qilib, "Saqlandi" xabarini yo'q qilardi.
  function replaceRow(updated: AppVersionRow) {
    setRows((prev) =>
      (prev ?? []).map((r) => (r.platform === updated.platform ? updated : r))
    );
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Ilova versiyasi</h1>
          <p className="page-sub">
            Mobil ilova ishga tushganda shu qiymatlarni o'qiydi va yangilash
            oynasini o'zi ko'rsatadi. O'zgarish deploysiz kuchga kiradi.
          </p>
        </div>
      </div>

      {err && <div className="error">{err}</div>}

      {!rows ? (
        <div className="empty">Yuklanmoqda…</div>
      ) : rows.length === 0 ? (
        <div className="empty">Platforma yozuvi yo'q</div>
      ) : (
        rows.map((row) => (
          <PlatformCard key={row.platform} row={row} onSaved={replaceRow} />
        ))
      )}
    </>
  );
}

function PlatformCard({
  row,
  onSaved,
}: {
  row: AppVersionRow;
  onSaved: (updated: AppVersionRow) => void;
}) {
  const [latest, setLatest] = useState(row.latest_version);
  const [minimum, setMinimum] = useState(row.min_supported_version);
  const [storeUrl, setStoreUrl] = useState(row.store_url);
  const [notes, setNotes] = useState(row.release_notes_uz ?? "");

  const [fieldErrors, setFieldErrors] = useState<Partial<Record<FieldKey, string>>>({});
  const [formErr, setFormErr] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmRaise, setConfirmRaise] = useState(false);

  // Qator qayta yuklangach (onSaved) inputlar serverdagi haqiqiy qiymatga qaytadi.
  useEffect(() => {
    setLatest(row.latest_version);
    setMinimum(row.min_supported_version);
    setStoreUrl(row.store_url);
    setNotes(row.release_notes_uz ?? "");
  }, [row]);

  async function save() {
    setBusy(true);
    setFieldErrors({});
    setFormErr(null);
    setOkMsg(null);
    const patch: AppVersionPatch = {
      latest_version: latest.trim(),
      min_supported_version: minimum.trim(),
      store_url: storeUrl.trim(),
      release_notes_uz: notes.trim() ? notes.trim() : null,
    };
    try {
      const updated = await api.updateAppVersion(row.platform, patch);
      setOkMsg("Saqlandi");
      onSaved(updated);
    } catch (e) {
      const code = e instanceof ApiError ? e.code : "";
      const field = ERROR_FIELD[code];
      // Xato aynan aybdor maydon tagida chiqadi — admin qaysi maydon
      // noto'g'riligini darhol ko'rsin.
      if (field) setFieldErrors({ [field]: (e as Error).message });
      else setFormErr((e as Error).message);
    } finally {
      setBusy(false);
      setConfirmRaise(false);
    }
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    // Faqat MINIMAL versiya ko'tarilganda tasdiq so'raladi — bu foydalanuvchini
    // bloklaydi. latest_version/havola/izoh o'zgarishi xavfsiz, dialogsiz ketadi.
    if (isRaise(row.min_supported_version, minimum.trim())) setConfirmRaise(true);
    else void save();
  }

  return (
    <div className="panel">
      <div className="panel-title">
        <h2>{APP_PLATFORM_LABEL[row.platform] ?? row.platform}</h2>
        <span className="muted">Oxirgi o'zgarish: {fmtDateTime(row.updated_at)}</span>
      </div>

      {formErr && <div className="error">{formErr}</div>}

      <form onSubmit={onSubmit}>
        <div className="form">
          <div className="grid-2">
            <div className="field">
              <label htmlFor={`latest-${row.platform}`}>Oxirgi versiya</label>
              <input
                id={`latest-${row.platform}`}
                className={fieldErrors.latest_version ? "invalid" : undefined}
                value={latest}
                onChange={(e) => { setOkMsg(null); setLatest(e.target.value); }}
                placeholder="1.1.0"
                inputMode="decimal"
              />
              {fieldErrors.latest_version && (
                <div className="field-error">{fieldErrors.latest_version}</div>
              )}
              <div className="help">
                Play Market'da mavjud eng yangi versiya. Bundan pastdagi
                foydalanuvchilar yumshoq yangilash taklifini ko'radi.
              </div>
            </div>

            <div className="field">
              <label htmlFor={`min-${row.platform}`}>
                Minimal qo'llab-quvvatlanadigan versiya
              </label>
              <input
                id={`min-${row.platform}`}
                className={fieldErrors.min_supported_version ? "invalid" : undefined}
                value={minimum}
                onChange={(e) => { setOkMsg(null); setMinimum(e.target.value); }}
                placeholder="1.0.1"
                inputMode="decimal"
              />
              {fieldErrors.min_supported_version && (
                <div className="field-error">{fieldErrors.min_supported_version}</div>
              )}
              <div className="help">
                Bundan pastdagi foydalanuvchilar ilovadan foydalana olmaydi —
                majburiy yangilash oynasi chiqadi.
              </div>
            </div>
          </div>

          <div className="field">
            <label htmlFor={`url-${row.platform}`}>Do'kon havolasi</label>
            <input
              id={`url-${row.platform}`}
              className={fieldErrors.store_url ? "invalid" : undefined}
              value={storeUrl}
              onChange={(e) => { setOkMsg(null); setStoreUrl(e.target.value); }}
              placeholder="https://play.google.com/store/apps/details?id=…"
            />
            {fieldErrors.store_url && (
              <div className="field-error">{fieldErrors.store_url}</div>
            )}
            <div className="help">
              https:// yoki market:// bilan boshlanishi kerak.
            </div>
          </div>

          <div className="field">
            <label htmlFor={`notes-${row.platform}`}>
              Yangilanish izohi (ixtiyoriy)
            </label>
            <textarea
              id={`notes-${row.platform}`}
              rows={4}
              value={notes}
              onChange={(e) => { setOkMsg(null); setNotes(e.target.value); }}
              placeholder="Nima yangilandi — foydalanuvchi shu matnni ko'radi"
            />
            <div className="help">
              Yangilash oynasida ko'rinadi. Bo'sh qoldirilsa hech narsa chiqmaydi.
            </div>
          </div>
        </div>

        <div className="actions">
          {okMsg && <span className="muted">{okMsg}</span>}
          <button type="submit" className="btn" disabled={busy}>
            {busy ? "..." : "Saqlash"}
          </button>
        </div>
      </form>

      {confirmRaise && (
        <ConfirmDialog
          title="Minimal versiyani ko'tarish"
          body={
            <>
              Minimal versiya <strong className="mono">{row.min_supported_version}</strong>{" "}
              → <strong className="mono">{minimum.trim()}</strong> ga ko'tarilmoqda.{" "}
              <strong className="mono">{minimum.trim()}</strong> dan pastdagi barcha
              foydalanuvchilar ilovadan foydalana olmaydi va majburan yangilanishi
              kerak bo'ladi. Davom etasizmi?
            </>
          }
          confirmLabel="Ha, o'zgartirish"
          cancelLabel="Bekor qilish"
          danger
          onCancel={() => setConfirmRaise(false)}
          onConfirm={save}
        />
      )}
    </div>
  );
}
